// @flow
import ConstraintCollection from "./input-constraints/ConstraintCollection";
import StringAttributeModel from "./StringAttributeModel";
import FileExtensionConstraint from "./input-constraints/FileExtensionConstraint";
import FileSizeConstraint from "./input-constraints/FileSizeConstraint";

import type {
  AttributeType,
  FilesType,
  FiletypeConstraintsType,
  FilesizeConstraintsType,
} from "../types";

const UNCHANGED = "UNCHANGED";

/**
 * Upload attribute
 */
export default class UploadAttributeModel extends StringAttributeModel {
  _files: FilesType;
  _initialTotalFileSize: number;
  _maxTotalFileSize: number;

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    return contributions.type === "binary";
  }

  /**
   */
  get type(): string {
    return "upload";
  }

  /**
   * Retrieve single or multi upload
   */
  get multiple(): boolean {
    return this.getContribution("multiple", false);
  }

  /**
   */
  get initialTotalFileSize(): number {
    return this._initialTotalFileSize || 0;
  }

  /**
   */
  set initialTotalFileSize(initialTotalFileSize: number) {
    this._initialTotalFileSize = initialTotalFileSize;
  }

  /**
   */
  get maxTotalFileSize(): number {
    return this._maxTotalFileSize;
  }

  /**
   */
  set maxTotalFileSize(maxTotalFileSize: number) {
    this._maxTotalFileSize = maxTotalFileSize;
  }

  /**
   */
  get currentFilesize(): number {
    return this.initialTotalFileSize + this.uploadedFileSize;
  }

  /**
   */
  get maxFileSize(): ?number {
    if (this.maxTotalFileSize) {
      const uploadMaxFileSize = this.maxTotalFileSize - this.currentFilesize;
      if (uploadMaxFileSize < 0) {
        return 0;
      }

      return uploadMaxFileSize;
    }

    return this.contributions.uploadMaxFileSize;
  }

  /**
   */
  get uploadedFileSize(): number {
    if (Object.keys(this.files).length === 0) {
      return 0;
    }

    let accumulatedFileSize = 0;
    Object.keys(this.files).forEach((fileName) => {
      const file = this.files[fileName];
      if (file.size) {
        accumulatedFileSize += file.size;
      }
    });
    return accumulatedFileSize;
  }

  /**
   * Upload constraints
   */
  get uploadConstraints(): {
    fileTypes: FiletypeConstraintsType,
    maxFileSize: FilesizeConstraintsType,
    acceptedFiles: Array<string>,
  } {
    const mimeTypes = this.contributions.allowedMimeTypes
      ? this.contributions.allowedMimeTypes.filter(
          (mimeType) => mimeType !== "[...]",
        )
      : [];
    const extensions = this.contributions.allowedExtensions
      ? this.contributions.allowedExtensions.filter(
          (extension) => extension !== "[...]",
        )
      : [];

    const fileTypes: FiletypeConstraintsType = extensions.map((extension) => ({
      extensions: [extension],
      mimeTypes,
    }));

    return {
      fileTypes,
      maxFileSize: {
        fileSize: this.maxFileSize,
        maxTotalFileSize: this.maxTotalFileSize,
        isMaxTotal: this.maxTotalFileSize !== undefined,
      },
      acceptedFiles: this.acceptedFiles,
    };
  }

  /**
   * Array of accepted file extensions and mime types.
   * Convenience method for accept attribute of file input html control
   */
  get acceptedFiles(): Array<string> {
    const mimeTypes = this.contributions.allowedMimeTypes
      ? this.contributions.allowedMimeTypes.filter(
          (mimeType) => mimeType !== "[...]",
        )
      : [];
    const extensions = this.contributions.allowedExtensions
      ? this.contributions.allowedExtensions
          .filter((extension) => extension !== "[...]")
          .map((extension) => `.${extension}`)
      : [];

    return [...extensions, ...mimeTypes];
  }

  /**
   * Add upload constraints
   */
  addConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    if (this.uploadConstraints.fileTypes.length > 0) {
      constraints.add(
        new FileExtensionConstraint(this.uploadConstraints.fileTypes),
      );
    }

    const hasFilesizeConstraint =
      this.uploadConstraints.maxFileSize.isMaxTotal ||
      this.uploadConstraints.maxFileSize.fileSize !== undefined;

    if (hasFilesizeConstraint) {
      constraints.add(
        new FileSizeConstraint(this.uploadConstraints.maxFileSize),
      );
    }

    return constraints;
  }

  /**
   */
  get files(): FilesType {
    return this._files || {};
  }

  /**
   */
  set files(files: FilesType) {
    this._files = files;
  }

  /**
   * Update the attribute
   */
  update(value: string): UploadAttributeModel {
    if (!value) {
      return this;
    }

    // parse to json and handle uploaded file
    // when the input value is a string but not json,
    // we assume that it is just a list of uploaded tokens and copy it as input value
    try {
      const files = JSON.parse(value);
      this.files = files;

      this.inputvalue = Object.keys(files)
        .map((fileName) => files[fileName].token)
        .join(",");
    } catch (error) {
      if (typeof value === "string") {
        this.inputvalue = value;
      }
    }

    this.updateLastModification();

    return this;
  }

  /**
   */
  get readonlyvalue(): string {
    if (this.initvalue) {
      return this.initvalue.toString();
    }

    return "";
  }

  /**
   * Getting the value of the attribute, return unchanged when the uploaded file has not changed
   */
  getValue(): any {
    if (!this.hasValue()) {
      return null;
    }

    if (this.initvalue === this.inputvalue) {
      return UNCHANGED;
    }

    return this._value;
  }

  /**
   */
  mergeAttribute(oldAttribute: AttributeType) {
    if (
      oldAttribute instanceof UploadAttributeModel &&
      oldAttribute.isValid &&
      oldAttribute.inputvalue !== null
    ) {
      this.update(JSON.stringify(oldAttribute.files));
    }
  }
}
